import { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, TablePagination, CircularProgress,
} from '@mui/material';
import { getLogsByModules } from '../../api/logApi';

const FONT = { fontFamily: "'Poppins', sans-serif" };

const ALL_MODULES = ['Pricing', 'Addon', 'Document', 'Notice', 'Campaign'];

const MODULE_COLORS = {
  Pricing:  { bg: '#e0f2fe', color: '#0369a1' },
  Addon:    { bg: '#fef3c7', color: '#92400e' },
  Document: { bg: '#e0e7ff', color: '#3730a3' },
  Notice:   { bg: '#fce7f3', color: '#9d174d' },
  Campaign: { bg: '#d1fae5', color: '#065f46' },
};

const ACTION_COLORS = {
  Created:  '#16a34a',
  Updated:  '#2563eb',
  Deleted:  '#dc2626',
  Approved: '#7c3aed',
  Rejected: '#ea580c',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  const day = d.getDate();
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year = d.getFullYear();
  const time = d.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${day} ${month} ${year}, ${time}`;
};

export default function ActivityLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [selectedModule, setSelectedModule] = useState('All');
  const [loading, setLoading] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const modules = selectedModule === 'All'
        ? ALL_MODULES.join(',')
        : selectedModule;
      const res = await getLogsByModules(modules, page + 1, pageSize);
      const data = res.data;
      setLogs(data.data || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch logs:', err);
      setLogs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [selectedModule, page, pageSize]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleModuleChange = (mod) => {
    setSelectedModule(mod);
    setPage(0);
  };

  const handlePageChange = (_, newPage) => setPage(newPage);

  const handleRowsPerPageChange = (e) => {
    setPageSize(parseInt(e.target.value, 10));
    setPage(0);
  };

  const filterChips = ['All', ...ALL_MODULES];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, width: '100%' }}>
      {/* Header */}
      <Typography variant="h5" sx={{ ...FONT, fontWeight: 600, mb: 0.5 }}>
        Activity Logs
      </Typography>
      <Typography variant="body2" sx={{ ...FONT, color: '#64748b', mb: 3 }}>
        Track changes to Resources, Noticeboard, and Incentives
      </Typography>

      {/* Module filter chips */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        {filterChips.map((mod) => {
          const isActive = selectedModule === mod;
          const modColor = mod !== 'All' ? MODULE_COLORS[mod] : null;
          return (
            <Chip
              key={mod}
              label={mod}
              clickable
              onClick={() => handleModuleChange(mod)}
              sx={{
                ...FONT,
                fontSize: '0.8rem',
                fontWeight: isActive ? 600 : 400,
                backgroundColor: isActive
                  ? (modColor ? modColor.bg : '#1e293b')
                  : '#f1f5f9',
                color: isActive
                  ? (modColor ? modColor.color : '#fff')
                  : '#475569',
                border: isActive ? 'none' : '1px solid #e2e8f0',
                '&:hover': {
                  backgroundColor: isActive
                    ? (modColor ? modColor.bg : '#334155')
                    : '#e2e8f0',
                },
              }}
            />
          );
        })}
      </Box>

      {/* Table */}
      <TableContainer sx={{
        border: '1px solid #e2e8f0',
        borderRadius: 2,
        backgroundColor: '#fff',
      }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f8fafc' }}>
              {['Date', 'User', 'Module', 'Action', 'Details'].map((col) => (
                <TableCell
                  key={col}
                  sx={{ ...FONT, fontWeight: 600, fontSize: '0.78rem', color: '#334155', py: 1.5 }}
                >
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ ...FONT, py: 6, color: '#94a3b8' }}>
                  No logs found
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => {
                const modStyle = MODULE_COLORS[log.module] || { bg: '#f1f5f9', color: '#475569' };
                const actionColor = ACTION_COLORS[log.action] || '#475569';
                return (
                  <TableRow key={log.logId} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell sx={{ ...FONT, fontSize: '0.78rem', color: '#64748b', whiteSpace: 'nowrap' }}>
                      {formatDate(log.createdOn)}
                    </TableCell>
                    <TableCell sx={{ ...FONT, fontSize: '0.78rem', color: '#334155' }}>
                      {log.createdBy || '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={log.module}
                        size="small"
                        sx={{
                          ...FONT,
                          fontSize: '0.72rem',
                          fontWeight: 500,
                          backgroundColor: modStyle.bg,
                          color: modStyle.color,
                          height: 24,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...FONT, fontSize: '0.78rem', fontWeight: 500, color: actionColor }}>
                      {log.action}
                    </TableCell>
                    <TableCell sx={{ ...FONT, fontSize: '0.78rem', color: '#475569', maxWidth: 360 }}>
                      {log.remarks || '-'}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handlePageChange}
        rowsPerPage={pageSize}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={[25, 50, 100]}
        sx={{ ...FONT, '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': FONT }}
      />
    </Box>
  );
}
